import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { ICP_TYPE_B2B, ICP_TYPE_B2C } from "../../constants";

// Detailed Profile View Component
export default ({
  profile,
  handleBackToGrid,
  handleDeleteProfile,
  setSelectedProfile,
  setEditorOpen,
  setViewMode,
}: {
  profile: any;
  handleBackToGrid: (e: any) => void;
  handleDeleteProfile: Function;
  setSelectedProfile: Function;
  setEditorOpen: Function;
  setViewMode: Function;
}) => {
  if (!profile) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button onClick={handleBackToGrid}>Back to Profiles</Button>
        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => {
              setSelectedProfile(profile);
              setEditorOpen(true);
              setViewMode("grid");
            }}
          >
            Edit Profile
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleDeleteProfile(profile.id)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Typography variant="h5">
            {profile.name}
          </Typography>
          <Chip
            label={profile.businessProfile?.icpType === ICP_TYPE_B2C ? "B2C" : "B2B"}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Demographics" : "Company Size"}
            </Typography>
            <Typography variant="body1">{profile.companySize}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Market Segment" : "Industry"}
            </Typography>
            <Typography variant="body1">{profile.industry}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Spending Range" : "Budget Range"}
            </Typography>
            <Typography variant="body1">{profile.budget}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Location
            </Typography>
            <Typography variant="body1">{profile.location}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Description
            </Typography>
            <Typography variant="body1">{profile.description}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Business Profile Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Consumer Profile" : "Business Profile"}
        </Typography>

        {profile.businessProfile?.industries?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Interest Areas & Categories" : "Industries & Sub-sectors"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.businessProfile.industries.map(
                (industry: string, idx: number) => (
                  <Chip key={idx} label={industry} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.businessProfile?.companySizeRange && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Demographic Details" : "Company Size Range"}
            </Typography>
            <Typography variant="body2">
              {profile.businessProfile.companySizeRange}
            </Typography>
          </Box>
        )}

        {profile.businessProfile?.revenue && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Income Range" : "Annual Revenue Range"}
            </Typography>
            <Typography variant="body2">
              {profile.businessProfile.revenue}
            </Typography>
          </Box>
        )}

        {profile.businessProfile?.geography && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Geographic Considerations
            </Typography>
            <Typography variant="body2">
              {profile.businessProfile.geography}
            </Typography>
          </Box>
        )}

        {profile.businessProfile?.businessCharacteristics?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Key Consumer Characteristics" : "Key Business Characteristics"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.businessProfile.businessCharacteristics.map(
                (char: string, idx: number) => (
                  <Chip key={idx} label={char} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Strategic Context Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Lifestyle Context" : "Strategic Context"}
        </Typography>

        {profile.strategicContext?.growthStage && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Life Stage" : "Growth Stage"}
            </Typography>
            <Typography variant="body2">
              {profile.strategicContext.growthStage}
            </Typography>
          </Box>
        )}

        {profile.strategicContext?.priorities?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Personal Priorities" : "Strategic Priorities"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.strategicContext.priorities.map(
                (priority: string, idx: number) => (
                  <Chip key={idx} label={priority} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.strategicContext?.values?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Personal Values & Lifestyle" : "Organizational Values & Culture"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.strategicContext.values.map(
                (value: string, idx: number) => (
                  <Chip key={idx} label={value} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Challenges & Buying Process Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Challenges & Purchase Behavior" : "Challenges & Buying Process"}
        </Typography>

        {profile.challenges?.painPoints?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Pain Points
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.challenges.painPoints.map(
                (point: string, idx: number) => (
                  <Chip key={idx} label={point} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.challenges?.needs?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Needs
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.challenges.needs.map((need: string, idx: number) => (
                <Chip key={idx} label={need} size="small" />
              ))}
            </Box>
          </Box>
        )}

        {profile.buyingProcess?.decisionMakers?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Key Influencers" : "Key Decision-makers"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
              {profile.buyingProcess.decisionMakers.map(
                (dm: any, idx: number) => {
                  // Handle both string and object formats
                  if (typeof dm === 'string') {
                    return <Chip key={idx} label={dm} size="small" />;
                  } else {
                    // For object format, display role and impact if available
                    const label = dm.role || dm.title || dm.name || JSON.stringify(dm);
                    return (
                      <Box key={idx} sx={{ mb: 1 }}>
                        <Chip label={label} size="small" sx={{ mb: 0.5 }} />
                        {dm.impact && (
                          <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
                            {dm.impact}
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                }
              )}
            </Box>
          </Box>
        )}

        {profile.buyingProcess?.evaluationCriteria?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Purchase Criteria" : "Critical Evaluation Criteria"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.buyingProcess.evaluationCriteria.map(
                (criteria: string, idx: number) => (
                  <Chip key={idx} label={criteria} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.buyingProcess?.objections?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Common Hesitations" : "Typical Objections"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.buyingProcess.objections.map(
                (objection: string, idx: number) => (
                  <Chip key={idx} label={objection} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Personas Section */}
      {profile.personas?.length > 0 &&
        profile.personas.map((persona: any, index: number) => (
          <Paper key={index} elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {profile.businessProfile?.icpType === ICP_TYPE_B2C
                ? `Consumer Type: ${persona.name || `Persona ${index + 1}`}`
                : `Persona: ${persona.name || `Persona ${index + 1}`}`}
            </Typography>

            {persona.role && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Demographics & Lifestyle" : "Professional Role"}
                </Typography>
                <Typography variant="body2">{persona.role}</Typography>
              </Box>
            )}

            {persona.profile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Consumer Profile" : "Professional Profile"}
                </Typography>
                <Typography variant="body2">{persona.profile}</Typography>
              </Box>
            )}

            {persona.challenges?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Pain Points & Frustrations" : "Daily Challenges"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {persona.challenges.map((challenge: string, idx: number) => (
                    <Chip key={idx} label={challenge} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {persona.motivations?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {profile.businessProfile?.icpType === ICP_TYPE_B2C ? "Buying Motivations" : "Key Motivations & Concerns"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {persona.motivations.map(
                    (motivation: string, idx: number) => (
                      <Chip key={idx} label={motivation} size="small" />
                    )
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        ))}

      {/* Go-to-Market Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Go-to-Market Recommendations
        </Typography>

        {profile.goToMarket?.channels?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Most Effective Marketing Channels
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.goToMarket.channels.map(
                (channel: string, idx: number) => (
                  <Chip key={idx} label={channel} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.goToMarket?.messaging?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Key Messaging Themes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.goToMarket.messaging.map(
                (message: string, idx: number) => (
                  <Chip key={idx} label={message} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {profile.goToMarket?.contentStrategies?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Content Opportunities
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {profile.goToMarket.contentStrategies.map(
                (content: string, idx: number) => (
                  <Chip key={idx} label={content} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
